import { UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  getDataAccessRequestsListPath,
  getLegacyExportAndAccessRequestDetailsPath,
  isAirlockRequestsV3RoutesEnabled
} from 'modules/requests/requestDetailPaths'
import { RESULT_STATUS_REFETCH_INTERVAL_MS } from 'modules/requests/utils'
import { validateResultLabel } from 'modules/resultsV2/labelValidation'
import { PARTICIPANT_LIST_REQUIRED_TOOLTIP, PERSON_ID_FILE_FAILED_MESSAGE } from 'modules/resultsV2/utils'
import { DataAccessRequestAttachmentSubmission, DataAccessRequestStatus } from 'services/data-access-requests/enums'
import { useApproveDataAccessRequest } from 'services/data-access-requests/hooks/useApproveDataAccessRequest'
import { useCreateDataAccessRequestLabel } from 'services/data-access-requests/hooks/useCreateDataAccessRequestLabel'
import { useDeleteDataAccessRequestLabel } from 'services/data-access-requests/hooks/useDeleteDataAccessRequestLabel'
import { useGetDataAccessRequestLabelsFilter } from 'services/data-access-requests/hooks/useGetDataAccessRequestLabelsFilter'
import { useGetDataAccessRequestVerification } from 'services/data-access-requests/hooks/useGetDataAccessRequestVerification'
import { useRejectDataAccessRequest } from 'services/data-access-requests/hooks/useRejectDataAccessRequest'
import { useRequestAdditionalInformationOnDataAccessRequest } from 'services/data-access-requests/hooks/useRequestAdditionalInformationOnDataAccessRequest'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type {
  DataAccessRequestVerificationDetail,
  RejectDataAccessRequestParams
} from 'services/data-access-requests/types'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import { DataAccessRequestParticipantFileStatus } from 'services/results/enums'
import type { DataAccessRequestParticipantFile, ResultFormSubmission, ResultLabel } from 'services/results/types'
import type { StudyId } from 'services/shared/schemas/studyIdSchema'
import type { ApiErrorResponse } from 'services/types'
import { useGetUserInfo } from 'services/user/hooks/useGetUserInfo'
import { useGetWorkspaceConfiguration } from 'services/workspaces/v2/hooks/useGetWorkspaceConfiguration'
import {
  hasWorkspaceConfiguration,
  isAutoSharingOnApprovalEnabledForWorkspaceConfiguration
} from 'services/workspaces/v2/utils'
import { isAdditionalInfoFormEnabled } from 'utils/env'

import {
  getIsRequestAdditionalInformationActionVisible,
  getLinkedChildRequestId,
  getLinkedParentRequestId
} from '../additionalInformationEligibility'
import { DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_DESCRIPTION } from '../constants'

export const getDataAccessRequestApprovalActionsDisabledReason = (
  participantFile?: DataAccessRequestParticipantFile
): string => {
  if (!participantFile || participantFile.status === DataAccessRequestParticipantFileStatus.Preparing) {
    return PARTICIPANT_LIST_REQUIRED_TOOLTIP
  }

  if (participantFile.status === DataAccessRequestParticipantFileStatus.Failed) {
    return PERSON_ID_FILE_FAILED_MESSAGE
  }

  if (
    participantFile.status === DataAccessRequestParticipantFileStatus.NotRequired ||
    participantFile.status === DataAccessRequestParticipantFileStatus.Ready
  ) {
    return ''
  }

  return PARTICIPANT_LIST_REQUIRED_TOOLTIP
}

export const shouldPollDataAccessRequestParticipantFile = (
  participantFile?: DataAccessRequestParticipantFile
): boolean => !participantFile || participantFile.status === DataAccessRequestParticipantFileStatus.Preparing

export const useDataAccessRequestDetails = (): {
  approveRequest: (studyId?: StudyId) => void
  approveRequestMutation: UseMutationResult<void, ApiErrorResponse, { studyId?: StudyId } | undefined>
  closeApprovalModal: () => void
  closeAdditionalInformationModal: () => void
  closeRejectionModal: () => void
  closeViewFormModal: () => void
  dataAccessRequest?: DataAccessRequestVerificationDetail
  parentDataAccessRequest?: DataAccessRequestVerificationDetail
  isLoadingParentDataAccessRequest: boolean
  isParentDataAccessRequestError: boolean
  approvalModalDescription: string
  availableLabels?: ResultLabel[]
  goBack: () => void
  isApprovalModalOpen: boolean
  autoSharingOnApprovalEnabled: boolean
  isApprovalActionsVisible: boolean
  isApproveActionVisible: boolean
  isApprovalConfigurationErrorAlertVisible: boolean
  approvalActionsDisabledReason: string
  isApprovalRestricted: boolean
  isParticipantFileDownloadDisabled: boolean
  isParticipantFileDownloadLoading: boolean
  isFailureAlertVisible: boolean
  isLoading: boolean
  isNotFound: boolean
  isAdditionalInformationModalOpen: boolean
  isRejectionModalOpen: boolean
  isViewFormModalOpen: boolean
  isRequestAdditionalInformationActionVisible: boolean
  openApprovalModal: () => void
  openAdditionalInformationModal: () => void
  openRejectionModal: () => void
  openViewFormModal: (
    formSubmission: ResultFormSubmission,
    attachmentSubmission?: DataAccessRequestAttachmentSubmission
  ) => void
  viewFormSubmission?: ResultFormSubmission
  viewFormAttachmentSubmission?: DataAccessRequestAttachmentSubmission
  requestAdditionalInformation: (reason: string) => void
  requestAdditionalInformationMutation: UseMutationResult<void, ApiErrorResponse, { reason: string }>
  requestAdditionalInformationModalDescription: string
  onAddDataAccessRequestLabel: (name: string) => void
  onAssignExistingDataAccessRequestLabel: (labelId: string) => void
  onDeleteDataAccessRequestLabel: (labelId: string) => void
  onValidateDataAccessRequestLabel: (name: string) => { isValid: boolean; message?: string }
  rejectRequest: (reason: string) => void
  rejectRequestMutation: UseMutationResult<void, ApiErrorResponse, RejectDataAccessRequestParams>
  rejectionModalDescription: string
  requestId?: string
} => {
  const { requestId } = useParams<{ requestId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)
  const [isAdditionalInformationModalOpen, setIsAdditionalInformationModalOpen] = useState(false)
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false)
  const [isViewFormModalOpen, setIsViewFormModalOpen] = useState(false)
  const [viewFormSubmission, setViewFormSubmission] = useState<ResultFormSubmission | undefined>()
  const [viewFormAttachmentSubmission, setViewFormAttachmentSubmission] = useState<
    DataAccessRequestAttachmentSubmission | undefined
  >()
  const isDataAccessDomainEnabled = isAirlockRequestsV3RoutesEnabled()

  useEffect(() => {
    if (!isDataAccessDomainEnabled && requestId) {
      navigate(getLegacyExportAndAccessRequestDetailsPath(requestId), { replace: true })
    }
  }, [isDataAccessDomainEnabled, navigate, requestId])

  const {
    data: dataAccessRequest,
    isLoading,
    isError
  } = useGetDataAccessRequestVerification({
    requestId: requestId ?? '',
    options: {
      refetchInterval: query =>
        shouldPollDataAccessRequestParticipantFile(query.state.data?.participantFile)
          ? RESULT_STATUS_REFETCH_INTERVAL_MS
          : false
    }
  })

  const linkedParentRequestId = getLinkedParentRequestId(dataAccessRequest)

  const {
    data: parentDataAccessRequest,
    isLoading: isLoadingParentDataAccessRequest,
    isError: isParentDataAccessRequestError
  } = useGetDataAccessRequestVerification({
    requestId: linkedParentRequestId ?? '',
    options: {
      enabled: isAdditionalInfoFormEnabled() && linkedParentRequestId !== undefined,
      onError: () => {}
    }
  })

  const labelsQuery = useGetDataAccessRequestLabelsFilter()

  const createDataAccessRequestLabelMutation = useCreateDataAccessRequestLabel({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.FilterLabels() })
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.Verification(variables.requestId) })
      invalidateRequestActivityQueries(queryClient)
    }
  })

  const deleteDataAccessRequestLabelMutation = useDeleteDataAccessRequestLabel(requestId ?? '', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.FilterLabels() })

      if (requestId) {
        queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.Verification(requestId) })
      }

      invalidateRequestActivityQueries(queryClient)
    }
  })

  const { data: userInfo } = useGetUserInfo()
  const sourceWorkspaceConfigurationQuery = useGetWorkspaceConfiguration(
    dataAccessRequest?.dataset?.sourceWorkspaceId ?? '',
    {
      enabled: !!dataAccessRequest?.dataset?.sourceWorkspaceId
    }
  )

  const requesterWorkspaceConfigurationQuery = useGetWorkspaceConfiguration(
    dataAccessRequest?.requesterContext.workspaceId ?? '',
    {
      enabled: !!dataAccessRequest?.requesterContext.workspaceId
    }
  )

  const goBack = (): void => {
    navigate(getDataAccessRequestsListPath())
  }

  const isUserTheRequester = userInfo?._id === dataAccessRequest?.requestingUser?._id
  const sourceWorkspaceConfiguration = sourceWorkspaceConfigurationQuery.data

  const isSelfReviewRestrictionEnabled =
    hasWorkspaceConfiguration(sourceWorkspaceConfiguration) &&
    !!sourceWorkspaceConfiguration.permissions.admins?.enableSelfReviewRestriction

  const isSourceWorkspaceConfigurationPending =
    !!dataAccessRequest?.dataset?.sourceWorkspaceId && !sourceWorkspaceConfigurationQuery.isSuccess
  const isSourceWorkspaceConfigurationLoading =
    isSourceWorkspaceConfigurationPending && !sourceWorkspaceConfigurationQuery.isError

  const isWorkspaceConfigPendingForRequester = isUserTheRequester && isSourceWorkspaceConfigurationPending

  const autoSharingOnApprovalEnabled =
    !!sourceWorkspaceConfigurationQuery &&
    isAutoSharingOnApprovalEnabledForWorkspaceConfiguration(sourceWorkspaceConfigurationQuery.data)

  const hasRequesterWorkspaceId = !!dataAccessRequest?.requesterContext.workspaceId

  // `autoSharingOnApprovalEnabled` reads `false` while the requester workspace configuration is
  // still loading, which would let a reviewer approve without the Study ID the flag makes
  // mandatory. Keep the approval actions hidden until that configuration resolves — but only
  // while it is in flight: a failed query is resolved, just not usable.
  const isRequesterWorkspaceConfigurationLoading =
    hasRequesterWorkspaceId &&
    !requesterWorkspaceConfigurationQuery.isSuccess &&
    !requesterWorkspaceConfigurationQuery.isError

  // When the configuration failed we cannot tell whether a Study ID is mandatory, so approval is
  // withheld. Rejection and requesting additional information never depend on it, so they stay.
  const isRequesterWorkspaceConfigurationError = hasRequesterWorkspaceId && requesterWorkspaceConfigurationQuery.isError

  const isApprovalRestricted = isUserTheRequester && isSelfReviewRestrictionEnabled
  const isParticipantFileDownloadDisabled = isApprovalRestricted || isWorkspaceConfigPendingForRequester
  const isParticipantFileDownloadLoading = isUserTheRequester && isSourceWorkspaceConfigurationLoading

  const isApprovalActionsVisible =
    dataAccessRequest?.status === DataAccessRequestStatus.PendingVerification &&
    getLinkedChildRequestId(dataAccessRequest) === undefined &&
    !isApprovalRestricted &&
    !isWorkspaceConfigPendingForRequester &&
    !isRequesterWorkspaceConfigurationLoading

  const isApproveActionVisible = isApprovalActionsVisible && !isRequesterWorkspaceConfigurationError

  const isApprovalConfigurationErrorAlertVisible = isApprovalActionsVisible && isRequesterWorkspaceConfigurationError

  const approvalActionsDisabledReason = getDataAccessRequestApprovalActionsDisabledReason(
    dataAccessRequest?.participantFile
  )

  const isRequestAdditionalInformationActionVisible =
    sourceWorkspaceConfigurationQuery.isSuccess &&
    getIsRequestAdditionalInformationActionVisible({
      dataAccessRequest,
      isApprovalActionsVisible,
      sourceWorkspaceConfiguration
    })

  const isFailureAlertVisible = dataAccessRequest?.status === DataAccessRequestStatus.Rejected

  const approvalModalDescription = `Do you want to approve the request from ${
    dataAccessRequest?.requestingUser?.email ?? 'this user'
  } to access ${dataAccessRequest?.dataset?.datasetName ?? 'this dataset'}?`

  const rejectionModalDescription = `The request to access ${
    dataAccessRequest?.dataset?.datasetName ?? 'this dataset'
  } will be rejected. Enter a reason for the rejection; it will be visible to the requester and other Airlock Managers.`

  const approveRequestMutation = useApproveDataAccessRequest({
    requestId: requestId ?? '',
    onSuccess: () => setIsApprovalModalOpen(false)
  })

  const rejectRequestMutation = useRejectDataAccessRequest({
    requestId: requestId ?? '',
    onSuccess: () => setIsRejectionModalOpen(false)
  })

  const requestAdditionalInformationMutation = useRequestAdditionalInformationOnDataAccessRequest({
    requestId: requestId ?? '',
    onSuccess: () => setIsAdditionalInformationModalOpen(false)
  })

  const requestAdditionalInformationModalDescription = DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_DESCRIPTION

  const openApprovalModal = (): void => setIsApprovalModalOpen(true)
  const closeApprovalModal = (): void => setIsApprovalModalOpen(false)
  const openAdditionalInformationModal = (): void => setIsAdditionalInformationModalOpen(true)
  const closeAdditionalInformationModal = (): void => setIsAdditionalInformationModalOpen(false)
  const openRejectionModal = (): void => setIsRejectionModalOpen(true)
  const closeRejectionModal = (): void => setIsRejectionModalOpen(false)
  const openViewFormModal = (
    formSubmission: ResultFormSubmission,
    attachmentSubmission?: DataAccessRequestAttachmentSubmission
  ): void => {
    setViewFormSubmission(formSubmission)
    setViewFormAttachmentSubmission(attachmentSubmission)
    setIsViewFormModalOpen(true)
  }
  const closeViewFormModal = (): void => {
    setIsViewFormModalOpen(false)
    setViewFormSubmission(undefined)
    setViewFormAttachmentSubmission(undefined)
  }

  const approveRequest = (studyId?: StudyId): void => {
    if (!requestId || approveRequestMutation.isPending) return

    if (autoSharingOnApprovalEnabled) {
      approveRequestMutation.mutate(studyId !== undefined && studyId !== '' ? { studyId } : {})
      return
    }

    approveRequestMutation.mutate(undefined)
  }

  const rejectRequest = (reason: string): void => {
    const trimmedReason = reason.trim()
    if (!requestId || !trimmedReason || rejectRequestMutation.isPending) return

    rejectRequestMutation.mutate({ reason: trimmedReason })
  }

  const requestAdditionalInformation = (reason: string): void => {
    const trimmedReason = reason.trim()
    if (
      !requestId ||
      !trimmedReason ||
      requestAdditionalInformationMutation.isPending ||
      !isRequestAdditionalInformationActionVisible ||
      !!approvalActionsDisabledReason
    ) {
      return
    }

    requestAdditionalInformationMutation.mutate({ reason: trimmedReason })
  }

  const onAddDataAccessRequestLabel = (name: string): void => {
    const trimmedName = name.trim()
    if (!requestId || !trimmedName || createDataAccessRequestLabelMutation.isPending) return

    createDataAccessRequestLabelMutation.mutate({ requestId, name: trimmedName })
  }

  const onAssignExistingDataAccessRequestLabel = (labelId: string): void => {
    const labelName = labelsQuery?.data?.data.find(label => label._id === labelId)?.name
    if (!labelName) return

    onAddDataAccessRequestLabel(labelName)
  }

  const onDeleteDataAccessRequestLabel = (labelId: string): void => {
    if (!requestId || deleteDataAccessRequestLabelMutation.isPending) return

    deleteDataAccessRequestLabelMutation.mutate({ labelId })
  }

  const onValidateDataAccessRequestLabel = validateResultLabel

  return {
    approveRequest,
    approvalModalDescription,
    approveRequestMutation,
    availableLabels: labelsQuery?.data?.data,
    closeAdditionalInformationModal,
    closeApprovalModal,
    closeRejectionModal,
    closeViewFormModal,
    dataAccessRequest,
    parentDataAccessRequest,
    isLoadingParentDataAccessRequest,
    isParentDataAccessRequestError,
    goBack,
    isApprovalModalOpen,
    autoSharingOnApprovalEnabled,
    isApprovalActionsVisible,
    isApproveActionVisible,
    isApprovalConfigurationErrorAlertVisible,
    approvalActionsDisabledReason,
    isApprovalRestricted,
    isParticipantFileDownloadDisabled,
    isParticipantFileDownloadLoading,
    isFailureAlertVisible,
    isLoading: isLoading || !isDataAccessDomainEnabled,
    isNotFound: isDataAccessDomainEnabled && !isLoading && (isError || !dataAccessRequest),
    isAdditionalInformationModalOpen,
    isRejectionModalOpen,
    isViewFormModalOpen,
    isRequestAdditionalInformationActionVisible,
    openApprovalModal,
    openAdditionalInformationModal,
    openRejectionModal,
    openViewFormModal,
    requestAdditionalInformation,
    requestAdditionalInformationMutation,
    requestAdditionalInformationModalDescription,
    onAddDataAccessRequestLabel,
    onAssignExistingDataAccessRequestLabel,
    onDeleteDataAccessRequestLabel,
    onValidateDataAccessRequestLabel,
    rejectRequest,
    rejectRequestMutation,
    rejectionModalDescription,
    requestId,
    viewFormAttachmentSubmission,
    viewFormSubmission
  }
}
